// ---------------------------------------------------------------------------
// AnimatedHeroLogo — hero logo animation for the landing page.
//
// Left:  BrandMark (blue sparkle star)
// Right: "YouAreYou" + Verified badge morphs in 3 stages:
//
//   Stage 0 (0–2s):   YouAreYou  +  Verified ✓
//   Stage 1 (2s):     u-ru.io/       (Verified fades out)
//   Stage 2 (3.2s):   u-ru.io/create your trust link
//
// Font: Public Sans (app default via --font-sans)
// ---------------------------------------------------------------------------

import { useState, useEffect } from "react";
import logoImg from "@/assets/logo.png";

const EASE = "cubic-bezier(0.65, 0, 0.35, 1)";
const SPEED = "0.8s";

export default function AnimatedHeroLogo() {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 2000);
    const t2 = setTimeout(() => setStage(2), 3200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Segments that collapse away
  const disappear: React.CSSProperties = {
    display: "inline-block",
    overflow: "hidden",
    transition: `max-width ${SPEED} ${EASE}, opacity ${SPEED} ${EASE}, letter-spacing ${SPEED} ${EASE}`,
    maxWidth: stage >= 1 ? "0" : "200px",
    opacity: stage >= 1 ? 0 : 1,
    letterSpacing: stage >= 1 ? "-20px" : "inherit",
  };

  // Segments that expand in at stage 1
  const reveal: React.CSSProperties = {
    display: "inline-block",
    overflow: "hidden",
    color: "#42454a",
    transition: `max-width ${SPEED} ${EASE}, opacity ${SPEED} ${EASE}`,
    maxWidth: stage >= 1 ? "200px" : "0",
    opacity: stage >= 1 ? 1 : 0,
  };

  // Tagline expands in at stage 2
  const tagline: React.CSSProperties = {
    display: "inline-block",
    overflow: "hidden",
    color: "#42454a",
    fontWeight: 400,
    fontSize: "0.65em",
    letterSpacing: "-0.5px",
    transition: `max-width ${SPEED} ${EASE}, opacity ${SPEED} ${EASE}`,
    maxWidth: stage >= 2 ? "600px" : "0",
    opacity: stage >= 2 ? 1 : 0,
    marginLeft: "6px",
  };

  // Verified row fades out and collapses at stage 1
  const verifiedRow: React.CSSProperties = {
    transition: "opacity 0.5s ease, transform 0.5s ease, max-height 0.5s ease",
    opacity: stage >= 1 ? 0 : 1,
    transform: stage >= 1 ? "translateY(8px)" : "translateY(0)",
    pointerEvents: stage >= 1 ? "none" : "auto",
    overflow: "hidden",
    maxHeight: stage >= 1 ? "0" : "60px",
  };

  return (
    <div className="inline-flex items-start gap-1.5 sm:gap-3">
      {/* Left: sparkle star */}
      <img src={logoImg} alt="" aria-hidden="true" className="hidden -mt-0.5 h-5 w-5 shrink-0 object-contain sm:block sm:h-8 sm:w-8" />

      {/* Right: animated text */}
      <div className="flex flex-col items-start">
        {/* Brand text row */}
        <div
          className="flex items-baseline whitespace-nowrap font-black leading-none tracking-tight"
          style={{ fontSize: "clamp(26px, 5vw, 28px)" }}
        >
          {/* Yo — disappears */}
          <span style={{ ...disappear, color: "#42454a" }}>Yo</span>
          {/* u — stays blue */}
          <span style={{ color: "#0052FF" }}>u</span>
          {/* A — disappears */}
          <span style={{ ...disappear, color: "#42454a" }}>A</span>
          {/* - — reveals */}
          <span style={reveal}>-</span>
          {/* r — stays blue */}
          <span style={{ color: "#0052FF" }}>r</span>
          {/* eYo — disappears */}
          <span style={{ ...disappear, color: "#42454a" }}>eYo</span>
          {/* u — stays blue */}
          <span style={{ color: "#0052FF" }}>u</span>
          {/* .io/ — reveals */}
          <span style={reveal}>.io/</span>
          {/* create your trust link — reveals at stage 2 */}
          <span style={tagline}>create your trust link</span>
        </div>

        {/* Verified row */}
        <div
          className="mt-1 flex items-center gap-2 sm:gap-3"
          style={verifiedRow}
        >
          <span
            className="font-normal tracking-tight text-gray-500"
            style={{ fontSize: "clamp(12px, 1.6vw, 20px)" }}
          >
            Verified
          </span>
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#3CB043] shadow-md sm:h-7 sm:w-7">
            <svg
              viewBox="0 0 12 12"
              fill="none"
              className="h-3 w-3 sm:h-4 sm:w-4"
              aria-hidden="true"
            >
              <path
                d="M2 6.5L4.8 9.2L10 3"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
